// First-session teach beats: Pop on the radio, once each, keyed off things this client already
// sees. Local only (no RPCs), remembered in the profile so a veteran never hears them twice.
// STORY_BIBLE §10.3 (bartender radio) is the home for this; the trainee run in the saloon will
// replace most of it later. Lines follow §9: deadpan, Pop stops short, never a wink.

import type { Profile } from './profile';
import { saveProfile } from './profile';

export type Beat =
  | 'inbound' | 'reload' | 'overheat' | 'prime' | 'primed' | 'teammate_down' | 'self_down' | 'cleared' | 'ping'
  | 'grenade' | 'cook' | 'crate' | 'special' | 'swap' | 'friendly'
  // the class moves (CLASSES.md), once per class the first time a robot's in front of you
  | 'move_valkyrie' | 'pound_valkyrie' | 'move_phantom' | 'blink_phantom' | 'cloak_phantom' | 'move_juggernaut' | 'hammer_juggernaut';

/** text may carry {primer}/{detonator}; glyph is a HUD bind id (BIND key) or '' */
const LINES: Record<Beat, { text: string; glyph: string }> = {
  inbound: { text: 'Units drop where the red brackets are. Be somewhere else when they land.', glyph: '' },
  reload: { text: 'Pack vents heat on every reload. Pick your moment.', glyph: 'reload' },
  overheat: { text: "You're cooking. Stop shooting, stop flying, let it breathe.", glyph: '' },
  prime: { text: 'Mark it first. {primer}. Then finish it. Company calls that depreciation.', glyph: 'primer' },
  primed: { text: "It's glowing. {detonator}. Don't waste it on a body shot.", glyph: '' },
  teammate_down: { text: "Partner's down. Get over there and hold it before the claim window shuts.", glyph: 'revive' },
  self_down: { text: 'Stay put. Your posse can pick you up.', glyph: '' },
  cleared: { text: "That's the ticket filled. Gate's charging.", glyph: '' },
  ping: { text: "Can't talk? Point. Tap it to mark, hold it to say something.", glyph: 'ping' },
  grenade: { text: 'You were issued grenades. The company would like them used.', glyph: 'grenade' },
  cook: { text: "Hold it and the fuse runs in your hand. Don't hold it that long.", glyph: 'grenade' },
  crate: { text: 'Supply crate on the ground. Walk over it. Hold the button to trade up.', glyph: 'revive' },
  special: { text: "That's the big one. Draw it, spend it, don't get sentimental.", glyph: 'special' },
  swap: { text: "Out? Don't reload in their face. Switch irons.", glyph: 'swap' },
  friendly: { text: 'That was your partner. The company bills for those.', glyph: '' },
  move_valkyrie: { text: 'Tap dodge twice. That one burns. Whatever you go through comes out marked.', glyph: 'ability' },
  pound_valkyrie: { text: 'Up high, hold the right stick in. Your shield goes down with you.', glyph: 'melee' },
  move_phantom: { text: "Tap dodge twice and you warp. Six metres, no walking. Don't do it hot.", glyph: 'ability' },
  blink_phantom: { text: "That one's close. Hold the right stick in. You end up behind it. It doesn't.", glyph: 'melee' },
  cloak_phantom: { text: "That smoke's you. They can't see you. Don't waste it.", glyph: '' },
  move_juggernaut: { text: "Tap dodge twice. That's the charge. It spends your whole shield. Spend it on something glowing.", glyph: 'ability' },
  hammer_juggernaut: { text: 'Hold the right stick in, then let go. The hammer settles anything glowing, shield or no shield.', glyph: 'melee' },
};

const SHOW_SECS = 6.5;
const GAP_SECS = 2.5;

export class Coach {
  current: { text: string; glyph: string; at: number } | null = null;
  private queue: { b: Beat; until: number }[] = [];
  private now = 0;
  private lastEnd = -99;

  constructor(private profile: Profile, private verbs: () => { primer: string; detonator: string; primerGlyph: string }) {}

  /** Ask for a beat. Ignored if it's been taught before or is already waiting. A beat tied to a
   *  moment (a crate on the ground) passes a ttl so it never plays after the moment's gone, and an
   *  urgent one jumps the queue. */
  want(b: Beat, ttl = Infinity, urgent = false) {
    if (this.profile.taught[b] || this.queue.some((q) => q.b === b) || this.current?.text === LINES[b].text) return;
    const q = { b, until: this.now + ttl };
    if (urgent) this.queue.unshift(q); else this.queue.push(q);
  }

  /** He did the thing: no need to tell him (and drop it if it was still waiting). */
  did(b: Beat) {
    if (this.profile.taught[b]) return;
    this.profile.taught[b] = 1; saveProfile(this.profile);
    this.queue = this.queue.filter((q) => q.b !== b);
  }

  update(time: number) {
    this.now = time;
    while (this.queue.length && this.queue[0].until < time) this.queue.shift();
    if (this.current && time - this.current.at > SHOW_SECS) { this.current = null; this.lastEnd = time; }
    if (!this.current && this.queue.length && time - this.lastEnd > GAP_SECS) {
      const b = this.queue.shift()!.b;
      if (this.profile.taught[b]) return;
      const v = this.verbs();
      const l = LINES[b];
      this.current = { text: l.text.replace('{primer}', cap(v.primer)).replace('{detonator}', cap(v.detonator)), glyph: l.glyph === 'primer' ? v.primerGlyph : l.glyph, at: time };
      this.profile.taught[b] = 1;
      saveProfile(this.profile);
    }
  }

  hud(time: number) { return this.current ? { text: this.current.text, glyph: this.current.glyph, age: time - this.current.at } : null; }
}

function cap(s: string) { const t = s.toLowerCase(); return t.charAt(0).toUpperCase() + t.slice(1); }
